import { useEffect, useState } from "react";
import { Link, useSearch, useLocation } from "wouter";
import { Search as SearchIcon, SearchX, Loader2, Package, Wrench, Building2, FileText, Briefcase, FolderKanban, ReceiptText } from "lucide-react";
import { SaveButton } from "../components/SaveButton";
import { api } from "../lib/api";

const META: Record<string, { Icon: any; label: string }> = {
  item: { Icon: Package, label: "Item" }, service: { Icon: Wrench, label: "Service" },
  company: { Icon: Building2, label: "Company" }, person: { Icon: FileText, label: "Résumé" },
  job: { Icon: Briefcase, label: "Job" }, project: { Icon: FolderKanban, label: "Project" }, rfq: { Icon: ReceiptText, label: "RFQ" },
};
const TYPE_ORDER = ["item", "service", "company", "person", "job", "project", "rfq"];

export default function Search() {
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const q = params.get("q") || "";
  const type = params.get("type") || "";
  const [box, setBox] = useState(q);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setBox(q); }, [q]);
  useEffect(() => {
    let alive = true;
    if (q.trim().length < 2) { setData({ results: [], counts: {}, total: 0 }); return; }
    setLoading(true);
    api.search(q, type || undefined)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ results: [], counts: {}, total: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [q, type]);

  function submit(e: React.FormEvent) { e.preventDefault(); if (box.trim().length >= 2) navigate(`/search?q=${encodeURIComponent(box.trim())}`); }
  function setType(t: string) { navigate(`/search?q=${encodeURIComponent(q)}${t ? `&type=${t}` : ""}`); }

  const counts = data?.counts || {};
  const totalAll = Object.values(counts).reduce((a: number, b: any) => a + (b || 0), 0);

  return (
    <div className="container-x py-6 max-w-3xl">
      <form onSubmit={submit} className="relative mb-5">
        <SearchIcon className="w-5 h-5 absolute left-4 top-3.5 text-mut" />
        <input autoFocus className="field pl-11 h-12 text-base" value={box} onChange={(e) => setBox(e.target.value)} placeholder="Search people, services, companies, jobs, projects, items…" />
      </form>

      {q.trim().length >= 2 && (
        <div className="flex gap-1.5 flex-wrap mb-4">
          <button onClick={() => setType("")} className={`chip ${!type ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>All ({totalAll})</button>
          {TYPE_ORDER.filter((t) => (counts[t] || 0) > 0).map((t) => (
            <button key={t} onClick={() => setType(t)} className={`chip capitalize ${type === t ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>{META[t].label} ({counts[t]})</button>
          ))}
        </div>
      )}

      {q.trim().length < 2 ? (
        <div className="card p-10 text-center text-mut"><SearchIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />Type at least 2 characters to search the marketplace.</div>
      ) : loading && !data?.results?.length ? (
        <div className="py-14 text-center text-mut"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (data?.results?.length ?? 0) === 0 ? (
        <div className="card p-10 text-center">
          <SearchX className="w-8 h-8 text-mut mx-auto mb-2" />
          <div className="font-bold text-navy">No results for “{q}”</div>
          <p className="text-sm text-mut mt-1">Try different keywords or fewer filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-mut mb-1">{data.results.length} result{data.results.length === 1 ? "" : "s"}{type ? ` in ${META[type]?.label}` : ""}</p>
          {data.results.map((r: any) => {
            const m = META[r.type] || { Icon: SearchIcon, label: r.type };
            const Icon = m.Icon;
            return (
              <div key={`${r.type}:${r.id}`} className="card p-3 flex items-center justify-between gap-3 hover:shadow-lift transition-shadow">
                <Link href={r.link} className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-navy-50 text-navy grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{r.title}</div>
                    <div className="text-xs text-mut truncate"><span className="chip bg-navy-50 text-navy-600 mr-1">{m.label}</span>{r.subtitle}</div>
                  </div>
                </Link>
                <SaveButton kind={r.type} refId={r.id} className="h-9 w-9 shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
